<template>
  <panel-layout
    id="courseRun-view-panel"
    ref="panel"
    :loading="loading"
    :show-footer="false"
  >
    <template #actions-right>
      <div v-if="courseRun" class="flex items-center space-x-2">
        <o-panel-link
          tag="o-button"
          flat
          icon="edit"
          :path="`/course/edit?idCourseRun=${courseRun.idCourseRun}&idCourse=${courseRun.course.idCourse}`"
        >
          {{ $t('pages.course.view.edit') }}
        </o-panel-link>
      </div>
    </template>

    <template #heading>
      <div v-if="courseRun" class="flex items-center space-x-2">
        <div>
          <h2
            class="text-lg md:text-xl tracking-tight font-bold text-gray-800 leading-none text-opacity-85 dark:text-gray-100"
          >
            {{ courseRun.course.name }} - {{ courseRun.information }}
          </h2>
        </div>
      </div>
    </template>

    <div v-if="courseRun" class="h-full flex flex-col">
      <!-- Course Details Section -->
      <div class="flex items-start space-x-4 p-6 border-b dark:border-gray-800">
        <div v-if="courseRun.course.image" class="flex-shrink-0">
          <img
            :src="courseRun.course.image"
            :alt="courseRun.course.name"
            class="w-24 h-24 rounded-lg object-cover"
          >
        </div>
        <div v-else class="flex-shrink-0 w-24 h-24 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
          <o-icon icon="course" :size="32" class="opacity-50" />
        </div>

        <div class="flex-1">
          <h3 class="text-lg font-semibold mb-1">
            {{ courseRun.course.name }}
          </h3>
          <p class="text-gray-600 dark:text-gray-400 mb-2">
            {{ courseRun.information }}
          </p>

          <div class="flex flex-wrap gap-4 text-sm">
            <div v-if="dateRangeLabel">
              <span class="text-gray-500 dark:text-gray-400">{{ $t('pages.course.view.dates') }}</span>
              <span class="ml-1 font-medium">{{ dateRangeLabel }}</span>
            </div>

            <div v-if="courseRun.paymentConfig.price">
              <span class="text-gray-500 dark:text-gray-400">{{ $t('pages.course.view.price') }}</span>
              <span class="ml-1 font-medium">{{ $currency.format(courseRun.paymentConfig.price) }}</span>
            </div>

            <div>
              <span class="text-gray-500 dark:text-gray-400">{{ $t('pages.course.view.enrolled') }}</span>
              <span class="ml-1 font-medium">{{ enrolmentLabel }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs Section -->
      <div class="flex-1 min-h-0">
        <o-tabs nav-class="px-5" scroll>
          <o-tab :name="$t('pages.course.edit.tabCustomers')" icon="customer" :alert="activeCustomers.length" initial>
            <course-customers
              :course-run="courseRun"
              :customers="customers"
              mode="view"
              @create-order="handleCreateOrder"
            />
          </o-tab>

          <o-tab :name="$t('pages.course.edit.tabSessions')" icon="calendar" :alert="courseRun.reservations?.length">
            <div class="px-4 pb-6">
              <div v-if="!courseRun.reservations?.length" class="flex flex-col justify-center items-center py-6">
                <p class="text-sm text-center text-gray-500 dark:text-gray-400">
                  {{ $t('pages.course.view.noSessionsYet') }}
                </p>
              </div>

              <div v-else class="space-y-2">
                <Session
                  v-for="(item, key) in sortedSessions"
                  :key="key"
                  :reservation="item.reservation"
                />
              </div>
            </div>
          </o-tab>
        </o-tabs>
      </div>
    </div>

    <create-order-dialog
      ref="createOrderDialog"
      @success="handleCreateOrderSuccess"
    />
  </panel-layout>
</template>

<script>
import Session from '@/components/courseRun/elements/Session'
import CourseCustomers from '@/components/courseRun/elements/CourseCustomers'
import PanelLayout from '@/components/panel/PanelLayout'
import CreateOrderDialog from '@/components/order/actions/CreateOrder'
import { OTabs, OTab } from '@/components/elements'

import { CustomerCourseStatus } from '@/_api/models/course'

export default {
  components: {
    Session,
    CourseCustomers,
    PanelLayout,
    CreateOrderDialog,
    OTabs,
    OTab
  },
  meta: {
    panel: true
  },
  inject: ['panel'],
  props: {
    idCourseRun: {
      type: String,
      default: null
    },
    idCourse: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      API: {
        CustomerCourseStatus,
      },
      courseRun: null,
      customers: null,
      loading: true
    }
  },
  computed: {
    activeCustomers () {
      return this.customers?.items?.filter(customer => customer.status !== CustomerCourseStatus.cancelled) || []
    },
    sortedSessions () {
      if (!this.courseRun?.reservations) {
        return []
      }
      return [...this.courseRun.reservations].sort((a, b) => {
        return new Date(a.reservation.date) - new Date(b.reservation.date)
      })
    },
    dateRangeLabel () {
      if (!this.courseRun?.dates?.length) {
        return null
      }
      const sorted = [...this.courseRun.dates].sort((a, b) => a - b)
      const firstDate = this.$date.convert(sorted[0], undefined, this.$date.presets.long)

      if (sorted.length === 1) {
        return firstDate
      }

      const lastDate = this.$date.convert(sorted[sorted.length - 1], undefined, this.$date.presets.long)
      return `${firstDate} - ${lastDate}`
    },
    enrolmentLabel () {
      const count = this.courseRun?.customerCounts?.customersCount || 0
      const max = this.courseRun?.customerCounts?.lowestCustomerMax

      if (max) {
        return `${count} / ${max}`
      }
      return count.toString()
    },
    isFull () {
      const available = this.courseRun?.customerCounts?.lowestSpace
      return available === 0
    }
  },
  async created () {
    await this.fetchCourseRun()
    await this.fetchCustomers()

    this.$bus.on.updated('CourseRun', this._uid, this.handleCourseRunUpdated)
    this.$bus.on.all('CustomerCourse', this._uid, this.handleCustomerCourseUpdated)

    this.loading = false
  },
  beforeUnmount () {
    this.$bus.off.all(this._uid)
  },
  methods: {
    async fetchCourseRun () {
      try {
        if (this.idCourseRun) {
          const response = await this.$api.course.getRunById(this.idCourseRun)
          this.courseRun = response.data
        }
      } catch (e) {
        console.error(e)
      }
    },
    async fetchCustomers () {
      try {
        if (this.courseRun) {
          this.customers = await this.$api.course.getRunCustomers(this.courseRun)
        }
      } catch (e) {
        console.error(e)
      }
    },
    handleCourseRunUpdated (courseRun) {
      if (courseRun.idCourseRun === this.courseRun?.idCourseRun) {
        this.fetchCourseRun()
      }
    },
    handleCustomerCourseUpdated () {
      this.fetchCustomers()
    },
    handleCreateOrder (customerCourse) {
      const customer = customerCourse.customer
      const date = new Date()
      const items = [customerCourse]

      const data = {
        customer,
        date,
        items
      }

      this.$refs.createOrderDialog.open(data)
    },
    handleCreateOrderSuccess () {
      this.fetchCustomers()
    }
  }
}
</script>
